import React from "react";
import { AnalyticsEvents, trackEvent } from "@/shared/lib/analytics";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { UserUpdateData } from "@/features/auth/lib/validation";
import { handleError, handleSilentError } from "@/shared/api/error-handler";
import { useGetMe, getMeQueryKey } from "@/shared/api/generated-kubb/hooks/useGetMe.ts";
import { UserProfile } from "@/shared/api/user";
import { AUTH_CONSTANTS, QUERY_SPECIFIC_CONFIG } from "@/shared/config";
import { AuthService } from "@/shared/lib/auth-service";
import { useAppNotifications } from "@/shared/lib/hooks/use-app-notifications-unified";
import { useAuthStore } from "@/shared/model/auth-store";

// KAN-975: валидаторы (zod) нужны только в действиях пользователя — вход, регистрация, правка профиля,
// OAuth. Статический import тащил zod и все схемы в общую оболочку каждой страницы, хотя useAuth
// сидит в хедере у всех, в том числе у анонимов.
const loadValidation = () => import("@/features/auth/lib/validation");


// Типы для мутаций
interface LoginCredentials {
  username: string;
  password: string;
}

interface RegistrationData {
  username: string;
  password: string;
  displayName?: string;
  email?: string;
}

interface OAuthCallbackData {
  provider: string;
  code: string;
  redirectUri: string;
  state?: string;
}

interface OAuthUrlData {
  provider: string;
  redirectUri: string;
  state?: string;
  returnPath?: string;
}

type UseGetMeResult = ReturnType<typeof useGetMe>;
type UseUserProfileResult = Omit<UseGetMeResult, "data"> & {
  data: UserProfile | null;
};

  // Хук для получения профиля пользователя
export function useUserProfile(forceRefresh = false): UseUserProfileResult {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useGetMe<UserProfile | null>({
    client: { timeout: AUTH_CONSTANTS.ME_REQUEST_TIMEOUT_MS },
    query: {
      enabled: isAuthenticated,
      staleTime: forceRefresh ? 0 : QUERY_SPECIFIC_CONFIG.USER.staleTime,
      gcTime: QUERY_SPECIFIC_CONFIG.USER.gcTime,
      // UserProfileDto (сген.) дрейфнул относительно доменного UserProfile —
      // мостим на границе (даунстрим типизирован как UserProfile).
      select: (data) => (data?.data ?? null) as unknown as UserProfile | null,
      retry: (failureCount: number, error: unknown) => {
        const status =
          typeof error === "object" && error !== null && "response" in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined;
        if (status === 401) {
          useAuthStore.getState().setIsAuthenticated(false);
          return false; // Не ретраим при 401
        }
        if (status === 403) {
          // Не ретраим при 403 (бан пользователя)
          return false;
        }
        return failureCount < 3;
      },
    },
  });

  // Синхронизируем isAuthenticated с данными из query (в useEffect чтобы избежать бесконечных циклов)
  React.useEffect(() => {
    if (query.data && !isAuthenticated) {
      useAuthStore.getState().setIsAuthenticated(true);
    }
  }, [query.data, isAuthenticated]);

  // Адаптируем ответ для совместимости (Kubb возвращает ResponseConfig с data внутри)
  // Данные уже нормализованы автоматически через Axios interceptor
  return {
    ...query,
    data: query.data ?? null,
  };
}


// Хук для логина
export function useLogin() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  const notifications = useAppNotifications();

  return useMutation({
    // Ошибку входа показывает форма — строкой под полями, по коду из ответа сервера.
    // Глобальный обработчик мутаций добавил бы поверх свой тост с неверной
    // классификацией (KAN-1216).
    meta: { skipGlobalErrorHandler: true },
    mutationFn: async ({ username, password }: LoginCredentials) => {
      const validation = (await loadValidation()).validateLoginCredentials({
        credentials: { username, password },
      });

      if (!validation.isValid) {
        throw new Error(validation.errors.join(", "));
      }

      const response = await AuthService.login({ username, password });

      if (!response.isSuccess) {
        throw new Error(response.error || tUi("litUseAuth.nevernyyLoginIliParol"));
      }

      return response;
    },
    onSuccess: () => {
      // Отмечаем как authenticated СНАЧАЛА: запрос /me гейтится по isAuthenticated
      // (enabled: isAuthenticated), поэтому без этого invalidate ниже — no-op, и вход
      // «не засчитывался» до перезагрузки страницы. Делаем как в email-code/register.
      useAuthStore.getState().setIsAuthenticated(true);
      // Инвалидируем кеш пользователя
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
      notifications.success(tUi("litUseAuth.vhodVypolnenUspeshno"));
    },
  });
}

// Хук для тестового входа (без пароля)
export function useTestLogin() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  const notifications = useAppNotifications();

  return useMutation({
    mutationFn: async () => {
      const response = await AuthService.testLogin();

      if (!response.isSuccess) {
        throw new Error(response.error || tUi("litUseAuth.oshibkaPriTestovomVhode"));
      }

      return response;
    },
    onSuccess: () => {
      // cookie-session: после тестового входа backend ставит sid cookie
      // Отмечаем как authenticated, иначе protected-страницы уйдут в редирект на /login
      useAuthStore.getState().setIsAuthenticated(true);
      
      // Инвалидируем кеш пользователя
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
      notifications.success(tUi("litUseAuth.testovyyVhodVypolnenUspeshno"));
    },
    onError: (error) => {
      notifications.error(error.message);
    },
  });
}

// Хук для регистрации
export function useRegister() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  const notifications = useAppNotifications();

  return useMutation({
    mutationFn: async ({
      username,
      password,
      displayName,
    }: RegistrationData) => {
      const validation = (await loadValidation()).validateRegistrationData({
        data: { username, password, displayName: displayName ?? "" },
      });

      if (!validation.isValid) {
        throw new Error(validation.errors.join(", "));
      }

      const response = await AuthService.register({
        username,
        password,
        displayName: displayName ?? "",
      });

      if (!response.isSuccess) {
        throw new Error(response.error || tUi("litUseAuth.oshibkaPriRegistratsii"));
      }

      return response;
    },
    onSuccess: (response) => {
      if (response.token) {
        // Инвалидируем кеш пользователя
        queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
      }
      notifications.success(tUi("litUseAuth.registratsiyaUspeshna"));
    },
    onError: (error) => {
      notifications.error(error.message);
    },
  });
}

// Хук для обновления профиля
export function useUpdateProfile() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  const notifications = useAppNotifications();

  return useMutation({
    mutationFn: async (userData: Partial<UserProfile>) => {
      const updateData: UserUpdateData = {
        ...(userData.displayName != null && {
          displayName: userData.displayName,
        }),
        ...(userData.about !== undefined && { about: userData.about }),
        ...(userData.location !== undefined && {
          location: userData.location,
        }),
        ...(userData.website !== undefined && { website: userData.website }),
        ...(userData.avatar !== undefined && { avatar: userData.avatar }),
        ...(userData.banner !== undefined && { banner: userData.banner }),
        ...(userData.languages != null && {
          languages: userData.languages,
        }),
        ...(userData.status != null && { status: userData.status }),
        ...(userData.socialLinks && {
          socialLinks: {
            discord: userData.socialLinks.discord ?? null,
            telegram: userData.socialLinks.telegram ?? null,
            x: userData.socialLinks.x ?? null,
            youtube: userData.socialLinks.youtube ?? null,
            twitch: userData.socialLinks.twitch ?? null,
          },
        }),
      };

      const validation = (await loadValidation()).validateUserUpdate({ data: updateData });

      if (!validation.isValid) {
        throw new Error(validation.errors.join(", "));
      }

      const normalizedPayload = {
        ...(updateData.displayName !== undefined && {
          displayName: updateData.displayName,
        }),
        ...(updateData.about !== undefined && { about: updateData.about }),
        ...(updateData.location !== undefined && {
          location: updateData.location,
        }),
        ...(updateData.website !== undefined && {
          website: updateData.website,
        }),
        ...(updateData.avatar !== undefined && {
          avatarUrl: updateData.avatar,
        }),
        ...(updateData.banner !== undefined && {
          banner: updateData.banner,
        }),
        ...(updateData.languages !== undefined && {
          languages: updateData.languages,
        }),
        ...(updateData.status !== undefined && {
          status: updateData.status,
        }),
        ...(updateData.socialLinks && {
          socialLinks: Object.fromEntries(
            Object.entries({
              x: updateData.socialLinks.x,
              discord: updateData.socialLinks.discord,
              telegram: updateData.socialLinks.telegram,
              youtube: updateData.socialLinks.youtube,
              twitch: updateData.socialLinks.twitch,
            }).filter(([_, v]) => v !== null && v !== undefined),
          ),
        }),
      };

      return AuthService.updateUserProfile(
        normalizedPayload as Parameters<typeof AuthService.updateUserProfile>[0],
      );
    },
    onSuccess: () => {
      // Инвалидируем кеш профиля (используем правильный ключ из useGetMe)
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
      notifications.success(tUi("litUseAuth.profilUspeshnoObnovlen"));
    },
    onError: (error) => {
      // Без дублирующего generic-тоста: handleError сам покажет реальное сообщение
      // (data.message). Раньше generic «Ошибка обновления профиля» маскировал причину
      // (напр. «email занят») и показывался вторым тостом.
      handleError(error, {
        component: "useUpdateProfile",
        action: "updateProfile",
      });
    },
  });
}

// Хук для OAuth логина
export function useOAuthLogin() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();

  return useMutation({
    retry: false, // OAuth код одноразовый — retry бессмысленен
    mutationFn: async ({
      provider,
      code,
      redirectUri,
      state,
    }: OAuthCallbackData) => {
      const validation = (await loadValidation()).validateOAuthProvider({ provider });
      if (!validation.isValid) {
        throw new Error(validation.error || tUi("litUseAuth.nedopustimyyOauthProvayder"));
      }

      const result = await AuthService.handleOAuthCallback({
        provider: validation.normalizedProvider!,
        code,
        redirectUri,
        state: state ?? "",
      });
      // Первый вход через провайдера = регистрация (бэкенд отдаёт isNewUser).
      // Раньше цель «Регистрация аккаунта» видела только регистрацию по email.
      if (result?.isNewUser === true) {
        trackEvent(AnalyticsEvents.UserRegistered, { method: validation.normalizedProvider! });
      }

      // Ждем сохранения токена
      await new Promise((resolve) =>
        setTimeout(resolve, AUTH_CONSTANTS.TOKEN_SAVE_DELAY),
      );

      // cookie-session: после OAuth backend ставит sid cookie, но Zustand не узнает об этом сам.
      // Важно: отмечаем как authenticated, иначе protected-страницы уйдут в редирект на /login.
      useAuthStore.getState().setIsAuthenticated(true);

      // KAN-1536: путь, с которым начинали вход (бэкенд хранит его в OAuth-флоу).
      return { returnPath: result?.returnPath ?? null };
    },
    onSuccess: () => {
      // Инвалидируем кеш пользователя
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    },
    onError: (error) => {
      handleSilentError(error, {
        component: "useOAuthLogin",
        action: "oauthLogin",
      });
      // Toast показывается в callback page с конкретным сообщением сервера
    },
  });
}

// Хук для получения OAuth URL
export function useOAuthUrl() {
  const tUi = useTranslations("Ui");
  return useMutation({
    mutationFn: async ({ provider, redirectUri, state, returnPath }: OAuthUrlData) => {
      const validation = (await loadValidation()).validateOAuthProvider({ provider });
      if (!validation.isValid) {
        throw new Error(validation.error || tUi("litUseAuth.nedopustimyyOauthProvayder"));
      }

      return AuthService.getOAuthUrl({
        provider: validation.normalizedProvider!,
        redirectUri,
        state: state ?? "",
        ...(returnPath ? { returnPath } : {}),
      });
    },
    onError: (error) => {
      handleSilentError(error, {
        component: "useOAuthUrl",
        action: "getOAuthUrl",
      });
    },
  });
}


// Хук для регистрации по email
export function useRegisterWithEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      username: string;
      password: string;
      displayName?: string;
      /** Согласие на обработку персональных данных (152-ФЗ). */
      pdnConsent: boolean;
      /** KAN-1362: «Новости GoodLuck» — только явная галочка. */
      newsOptIn?: boolean;
      /** KAN-1103: токен Cloudflare Turnstile. */
      turnstileToken?: string;
    }) => {
      return AuthService.registerWithEmail(data);
    },
    onSuccess: (response) => {
      if (response.isSuccess && !response.emailVerificationRequired) {
        useAuthStore.getState().setIsAuthenticated(true);
        queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
      }
    },
  });
}

// Хук для отправки кода на email
export function useSendEmailCode() {
  return useMutation({
    mutationFn: async ({
      email,
      purpose,
      turnstileToken,
    }: {
      email: string;
      purpose: "email_verification" | "passwordless_login" | "password_reset";
      /** KAN-1103: токен Cloudflare Turnstile. */
      turnstileToken?: string;
    }) => {
      return AuthService.sendEmailCode(email, purpose, turnstileToken);
    },
  });
}

// Хук для верификации email
export function useVerifyEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      return AuthService.verifyEmail(email, code);
    },
    onSuccess: () => {
      useAuthStore.getState().setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    },
  });
}

// Хук для входа по email+код.
// Ответ может означать «код верный, но аккаунта ещё нет» — тогда сессии не появилось,
// и помечать пользователя авторизованным нельзя: интерфейс решил бы, что вход состоялся.
export function useLoginWithEmailCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      return AuthService.loginWithEmailCode(email, code);
    },
    onSuccess: (data) => {
      if (data?.registrationRequired) return;
      useAuthStore.getState().setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    },
  });
}

// Хук создания аккаунта после подтверждённого кода
export function useCompleteEmailSignup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      email: string;
      code: string;
      username: string;
      displayName?: string;
      pdnConsent: boolean;
      /** KAN-1362: «Новости GoodLuck» — только явная галочка. */
      newsOptIn?: boolean;
    }) => {
      return AuthService.completeEmailSignup(payload);
    },
    onSuccess: () => {
      useAuthStore.getState().setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    },
  });
}

// Хук для установки пароля
export function useSetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newPassword: string) => {
      return AuthService.setPassword(newPassword);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getMeQueryKey() }),
  });
}

// Хук для смены пароля
export function useChangePassword() {
  return useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => {
      return AuthService.changePassword(currentPassword, newPassword);
    },
  });
}

// Хук для сброса пароля
export function useResetPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      code,
      newPassword,
    }: {
      email: string;
      code: string;
      newPassword: string;
    }) => {
      return AuthService.resetPassword(email, code, newPassword);
    },
    onSuccess: () => {
      useAuthStore.getState().setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    },
  });
}

// Хук для проверки доступности username
export function useCheckUsername() {
  return useMutation({
    mutationFn: async (username: string) => {
      return AuthService.checkUsername(username);
    },
  });
}

// Хук для выхода
export function useLogout() {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  const notifications = useAppNotifications();

  return useMutation({
    onMutate: () => {
      // Сначала локально выходим (чтобы не было "автологина" из кеша/стора),
      // затем пытаемся отозвать сессию на сервере.
      useAuthStore.getState().logout();
      queryClient.clear();
    },
    mutationFn: async () => {
      await AuthService.logout();
    },
    onSuccess: () => {
      notifications.success(tUi("litUseAuth.vyVyshliIzAkkaunta"));
    },
    onError: (error) => {
      notifications.error(tUi("litUseAuth.neUdalosZavershitSessiyu"));
      handleError(error, {
        component: "useLogout",
        action: "logout",
      });
    },
  });
}
